import { Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useFeedFilterStore } from '@/stores/feedFilterStore';
import { cn } from '@/lib/utils';

const FRAMEWORKS = [
  'HIPAA',
  'FDA 21 CFR',
  'ISO 13485',
  'SOC 2',
  'Joint Commission',
  'OSHA',
  'CMS',
] as const;

const URGENCY_LEVELS = [
  { label: 'Critical', value: 'critical', color: 'bg-compliance-critical' },
  { label: 'High', value: 'high', color: 'bg-compliance-high' },
  { label: 'Medium', value: 'medium', color: 'bg-compliance-medium' },
  { label: 'Low', value: 'low', color: 'bg-compliance-low' },
] as const;

export function FeedFilterPopover() {
  const { frameworks, urgencyLevels, toggleFramework, toggleUrgency, clearFilters } =
    useFeedFilterStore();

  const activeCount = frameworks.length + urgencyLevels.length;

  return (
    <Popover>
      <PopoverTrigger>
        <Button variant="outline" size="sm" className="gap-2">
          <Filter className="h-3.5 w-3.5" />
          Filters
          {activeCount > 0 && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-teal text-[10px] font-bold text-white">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-4" align="end">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground">Filters</p>
          {activeCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs text-muted-foreground"
              onClick={clearFilters}
            >
              <X className="mr-1 h-3 w-3" />
              Clear all
            </Button>
          )}
        </div>

        <div className="mt-4 space-y-4">
          {/* Frameworks */}
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Frameworks
            </p>
            <div className="flex flex-wrap gap-1.5">
              {FRAMEWORKS.map((fw) => (
                <button
                  key={fw}
                  type="button"
                  onClick={() => toggleFramework(fw)}
                  className={cn(
                    'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                    frameworks.includes(fw)
                      ? 'bg-teal text-white'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80',
                  )}
                >
                  {fw}
                </button>
              ))}
            </div>
          </div>

          {/* Urgency */}
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Urgency
            </p>
            <div className="flex flex-wrap gap-1.5">
              {URGENCY_LEVELS.map((level) => (
                <button
                  key={level.value}
                  type="button"
                  onClick={() => toggleUrgency(level.value)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                    urgencyLevels.includes(level.value)
                      ? 'bg-teal text-white'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80',
                  )}
                >
                  <span className={cn('h-2 w-2 rounded-full', level.color)} />
                  {level.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
